/**
 * Account Updates Hook
 * Subscribe to real-time account balance and position updates via WebSocket
 */

import { useEffect, useState, useCallback } from 'react'
import { useWebSocketContext } from '../contexts/WebSocketContext'
import logger from '../utils/logger'

const logConnection = msg => logger.debug('[AccountUpdates]', msg)
const logDebug = (msg, data) => logger.debug('[AccountUpdates]', msg, data)

export const useAccountUpdates = (options = {}) => {
  const {
    onAccountUpdate,
    onPositionUpdate,
    accountId,
    autoSubscribe = true
  } = options
  const ws = useWebSocketContext()
  const [accountData, setAccountData] = useState(null)
  const [positions, setPositions] = useState([])
  const [latestUpdate, setLatestUpdate] = useState(null)

  /**
   * Handle account update from WebSocket
   */
  const handleAccountUpdate = useCallback(
    message => {
      const { type, data, timestamp } = message

      if (type === 'account_update') {
        logDebug('Received account update:', data)

        // Filter by accountId if specified
        if (accountId && data.accountId && data.accountId !== accountId) {
          return
        }

        // Update account data
        setAccountData(prev => ({
          ...prev,
          ...data,
          lastUpdated: timestamp
        }))

        setLatestUpdate({
          type: 'account',
          data,
          timestamp
        })

        // Call custom callback if provided
        if (onAccountUpdate) {
          onAccountUpdate(data)
        }
      }
    },
    [accountId, onAccountUpdate]
  )

  /**
   * Handle position update from WebSocket
   */
  const handlePositionUpdate = useCallback(
    message => {
      const { type, data, timestamp } = message

      if (type === 'position_update') {
        logDebug('Received position update:', data)

        // Filter by accountId if specified
        if (accountId && data.accountId && data.accountId !== accountId) {
          return
        }

        // Update positions array
        setPositions(prev => {
          const existingIndex = prev.findIndex(p => p.symbol === data.symbol)

          if (existingIndex >= 0) {
            // Update existing position
            const updated = [...prev]
            updated[existingIndex] = { ...data, lastUpdated: timestamp }
            return updated
          } else {
            // Add new position
            return [...prev, { ...data, lastUpdated: timestamp }]
          }
        })

        setLatestUpdate({
          type: 'position',
          data,
          timestamp
        })

        // Call custom callback if provided
        if (onPositionUpdate) {
          onPositionUpdate(data)
        }
      }
    },
    [accountId, onPositionUpdate]
  )

  /**
   * Subscribe to account and position updates
   */
  useEffect(() => {
    if (!ws.isAuthenticated || !autoSubscribe) {
      return
    }

    logConnection('Subscribing to account and position updates')

    // Subscribe to accounts and positions channels
    ws.subscribe(['accounts', 'positions'])

    // Register message handlers
    const unsubscribeAccount = ws.on('account_update', handleAccountUpdate)
    const unsubscribePosition = ws.on('position_update', handlePositionUpdate)

    // Cleanup
    return () => {
      logConnection('Unsubscribing from account and position updates')
      ws.unsubscribe(['accounts', 'positions'])
      unsubscribeAccount()
      unsubscribePosition()
    }
  }, [
    ws.isAuthenticated,
    ws,
    autoSubscribe,
    handleAccountUpdate,
    handlePositionUpdate
  ])

  /**
   * Clear account data
   */
  const clearAccountData = useCallback(() => {
    setAccountData(null)
    setPositions([])
    setLatestUpdate(null)
  }, [])

  /**
   * Get position by symbol
   */
  const getPositionBySymbol = useCallback(
    symbol => {
      return positions.find(p => p.symbol === symbol)
    },
    [positions]
  )

  /**
   * Get open positions
   */
  const getOpenPositions = useCallback(() => {
    return positions.filter(p => p.quantity > 0)
  }, [positions])

  /**
   * Get total position value
   */
  const getTotalPositionValue = useCallback(() => {
    return positions.reduce((sum, p) => sum + (p.currentValue || 0), 0)
  }, [positions])

  /**
   * Get total unrealized P&L
   */
  const getTotalUnrealizedPnL = useCallback(() => {
    return positions.reduce((sum, p) => sum + (p.unrealizedPnL || 0), 0)
  }, [positions])

  return {
    accountData,
    positions,
    latestUpdate,
    clearAccountData,
    getPositionBySymbol,
    getOpenPositions,
    getTotalPositionValue,
    getTotalUnrealizedPnL,
    isConnected: ws.isConnected,
    isAuthenticated: ws.isAuthenticated
  }
}
